import "./CalculatorUnit.scss";
// REACT; REDUX; ROUTER
import React, { ChangeEvent, useEffect, useState } from "react";

// COMPONENTS & FUNCTIONS
import {
  addInputField,
  calculateInputField,
  checkEmptyInputs,
  checkInput,
  clearCalc,
  convertAllUnits,
  convertUnit,
  populateReceipt,
  updateDecimals,
  updateInputField,
  updatePriorityList,
} from "../../functions/unitHandler";
import { removeFormatLargeNumbers } from "../../functions/textFormatHandler";
import { addAnimation } from "../../functions/animationHandler";
import { updateAppDataFromLocalStorage } from "../../functions/localStorageHandlers";

// INTERFACES
import { BaseQuantity } from "../../interface/functionalityInterface";
import { ConverterUnitProps } from "../../interface/propTypesInterface";

// NPM INSTALLS
import * as FaIcons from "react-icons/fa";
import CalculatorFieldset from "./CalculatorFieldset";

/*
  CONVTERTERUNIT COMPONENT INFORMATION

*/

export default function ConverterUnit(props: ConverterUnitProps) {
  // ::::: CONSTANTS :::::
  const { removeUnit, duplicateUnit, addToReceipt } = props;
  const listOfCalcmodes = props.inputs.map((calcMode) => calcMode.name);

  // ::::: USEHOOKS :::::

  // entry animation of calculator
  useEffect(() => {
    const calcHtmlElement = document.getElementById(calc.id);
    setcalcElem(calcHtmlElement);
    if (!calcHtmlElement) return;
    calcHtmlElement.classList.add("show");
  }, []);

  // Change state if props changes
  useEffect(() => {
    setcalc({ ...props });
  }, [props]);

  // ::::: STATES :::::
  const [calc, setcalc] = useState<ConverterUnitProps>(props);
  const [calcElem, setcalcElem] = useState<HTMLElement | null>();

  // ::::: HANDLER FUNCTIONS :::::

  const handleInputs = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    // Check if input has prohibited characters
    const targetValue = removeFormatLargeNumbers(e.target.value).replace(
      ",",
      "."
    );
    if (!checkInput(targetValue, calc.enableOperators)) {
      addAnimation(e.target, "shakeHorError");
      return;
    }

    // Update priority list to convert all field but the one the user typed in
    const updatedPriorityList = updatePriorityList(calc, e.target.id);

    // Update input field with user input value
    const calcWithUpdatedInput = updateInputField(
      updatedPriorityList,
      e.target.id,
      targetValue
    ) as ConverterUnitProps;

    // Find the id of the last inputted field
    const typedFieldId =
      calcWithUpdatedInput.priorityList[
        calcWithUpdatedInput.priorityList.length - 1
      ];

    // Match Id of last inputted field with basequantity
    const baseQuantity = calcWithUpdatedInput.inputs[
      calcWithUpdatedInput.selectedCalcMode
    ].baseQuantities.find((bq) => bq.id === typedFieldId);

    if (!baseQuantity)
      throw new Error(`Could not find base quantity with ID ${typedFieldId}`);

    // Give conversions
    const calcWithConvertedFields = convertAllUnits(
      calcWithUpdatedInput,
      baseQuantity
    );
    setcalc(calcWithConvertedFields);
    // Update calculator in local storage
    updateAppDataFromLocalStorage(calc.id, calcWithConvertedFields);
    // localStorage.setItem(calc.id, JSON.stringify(calcWithConvertedFields)); meTOOD remove
  };

  const handleTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    // If I want to constrict the title input
    // if (!checkTitle(e.target.value)) {
    //   addAnimation(e.target, "shakeHorError");
    //   return;
    // }
    const calculator = {
      ...calc,
      name: e.target.value,
    };

    setcalc(calculator);
    // Update calculator in local storage
    updateAppDataFromLocalStorage(calc.id, calculator);
  };

  const handleUnits = (
    formInput: BaseQuantity,
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    // Selected unit from select dropdown
    const index = e.target.options.selectedIndex;
    const unit = e.target.options[index].value;

    // Convert the selected unit
    const calcWithConvertedField = convertUnit(
      calc,
      formInput,
      unit
    ) as ConverterUnitProps;

    // Id of the input that user last inputted
    const typedFieldId =
      calcWithConvertedField.priorityList[
        calcWithConvertedField.priorityList.length - 1
      ];

    // Find last inputted Id in baseQuantity list of the selected calcmode
    const baseQuantity = calcWithConvertedField.inputs[
      calcWithConvertedField.selectedCalcMode
    ].baseQuantities.find((bq) => bq.id === typedFieldId);

    if (!baseQuantity)
      throw new Error(`Could not find base quantity with ID ${typedFieldId}`);

    if (checkEmptyInputs(calcWithConvertedField) <= 1) {
      const calcWithAllConvertedFields = convertAllUnits(
        calcWithConvertedField,
        baseQuantity
      );
      setcalc(calcWithAllConvertedFields);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, calcWithConvertedField);
    } else {
      setcalc(calcWithConvertedField);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, calcWithConvertedField);
    }
  };

  const handleCalcMode = (e: ChangeEvent<HTMLSelectElement>) => {
    // Find the calc mode based on selection from select menu
    const selectedIndex = e.target.options.selectedIndex;
    const calcMode = e.target.options[selectedIndex].value;

    // Match calcmode with selected option
    const index = calc.inputs.findIndex((mode) => mode.name === calcMode);
    if (index === -1) return;

    // reset all fields of new calc mode
    const calcWithNewCalcMode = {
      ...calc,
      selectedCalcMode: index,
      priorityList: calc.inputs[index].baseQuantities.map((bq) => bq.id),
      inputs: calc.inputs.map((input) => {
        return {
          ...input,
          baseQuantities: input.baseQuantities.map((bq) => {
            return {
              ...bq,
              value: "",
            };
          }),
        };
      }),
    };
    setcalc(calcWithNewCalcMode);
    // Update calculator in local storage
    updateAppDataFromLocalStorage(calc.id, calcWithNewCalcMode);
  };

  const handleReceipt = () => {
    const receipt = populateReceipt(calc);
    if (receipt) {
      if (calcElem) addAnimation(calcElem, "shakeVertSuccess");
      addToReceipt(receipt);
    } else {
      if (calcElem) addAnimation(calcElem, "shakeHorError");
      addToReceipt(null);
    }
  };

  const handleDecimal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    changeAmount: "increase" | "decrease"
  ) => {
    let decimal = calc.noDecimals;
    if (changeAmount === "increase") {
      // no more than 9 decimals
      if (calc.noDecimals !== 9) {
        decimal++;
      }
    } else {
      // no less then 0 decimals
      if (calc.noDecimals !== 0) {
        decimal--;
      }
    }
    const updatedDecimals: ConverterUnitProps = {
      ...calc,
      noDecimals: decimal,
    };

    // get last input field, all will be converted except that one
    const typedFieldId =
      updatedDecimals.priorityList[updatedDecimals.priorityList.length - 1];
    // find last input field in calculator
    const baseQuantity = updatedDecimals.inputs[
      updatedDecimals.selectedCalcMode
    ].baseQuantities.find((bq) => bq.id === typedFieldId);

    if (!baseQuantity)
      throw new Error(
        `CovnerterUnit-handleDecimal: Could not find base quantity with ID ${typedFieldId}`
      );
    if (
      checkEmptyInputs(updatedDecimals) <
      calc.inputs[calc.selectedCalcMode].baseQuantities.length
    ) {
      const convertedCalc = convertAllUnits(updatedDecimals, baseQuantity);
      setcalc(convertedCalc);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, convertedCalc);
    } else {
      setcalc(updatedDecimals);
      // Update calculator in local storage
      updateAppDataFromLocalStorage(calc.id, updatedDecimals);
    }
  };

  const handleClearCalc = () => {
    const calculator = clearCalc(calc) as ConverterUnitProps;
    setcalc(calculator);
    updateAppDataFromLocalStorage(calc.id, calculator);

    if (calcElem) addAnimation(calcElem, "shakeVert");
  };

  const handleAddInput = () => {
    const calculator = addInputField(calc) as ConverterUnitProps;
    setcalc(calculator);
    updateAppDataFromLocalStorage(calc.id, calculator);
  };

  const handleRemoveInput = (inputId: string) => {
    // Note that priority list needs to be updated as well in order for this to work
    const calculator: ConverterUnitProps = {
      ...calc,
      priorityList: calc.priorityList.filter((bq) => bq !== inputId),
      inputs: calc.inputs.map((calcMode, calcModeIndex) => {
        return {
          ...calcMode,
          baseQuantities:
            calc.selectedCalcMode === calcModeIndex
              ? calcMode.baseQuantities.filter((bq) => bq.id !== inputId)
              : calcMode.baseQuantities,
        };
      }),
    };
    setcalc(calculator);
    updateAppDataFromLocalStorage(calc.id, calculator);
  };
  // ::::: JSX :::::
  try {
    return (
      <div id={calc.id} className="calculator">
        <form>
          {/* ---------------------- TOP TOOLBAR ----------------------*/}
          <div className="calculator__buttons">
            <button
              type="button"
              onClick={() => removeUnit(calc.id)}
              className="calculator__delete round-button"
              data-tooltip="remove calc"
            >
              <FaIcons.FaTrash />
            </button>
            <button
              type="button"
              onClick={handleClearCalc}
              className="round-button"
              data-tooltip="clear calc"
            >
              <FaIcons.FaBan />
            </button>
          </div>
          {/* ---------------------- FIGURE ----------------------*/}
          <div className="calculator__figure">
            {calc.inputs[calc.selectedCalcMode].figure ? (
              <img
                id="calc-figure-img"
                src={calc.inputs[calc.selectedCalcMode].figure}
                draggable="false"
              />
            ) : (
              <span>{calc.name.substring(0, 5)}</span>
            )}
          </div>
          {/* ---------------------- TITLE ----------------------*/}
          <fieldset className="calculator__title">
            <label>calculator name</label>
            <input
              value={calc.name}
              onChange={handleTitle}
              className={calc.inputs.length <= 1 ? "no-dropdown" : ""}
              style={{
                textAlign: "center",
              }}
              placeholder="calculator name"
            />
          </fieldset>
          {/* ---------------------- CALCULATOR MODE SELECT ----------------------*/}
          {listOfCalcmodes.length > 1 && (
            // meTODO: add arrow down next to button
            <div data-tooltip="change calcmode">
              <select
                name={calc.inputs[calc.selectedCalcMode].name.replaceAll(
                  "_",
                  " "
                )}
                defaultValue={calc.inputs[calc.selectedCalcMode].name}
                onChange={(e) => handleCalcMode(e)}
                className="calculator__calcmode"
              >
                {listOfCalcmodes.map((calcmode, index) => {
                  return (
                    <option key={`${calcmode}_${index}`} value={calcmode}>
                      {calcmode.replaceAll("_", " ")}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          <span className="divider"></span>
          {/* ---------------------- INPUTS ----------------------*/}
          <div className="calculator__inputs">
            {calc.inputs[calc.selectedCalcMode].baseQuantities.map((bq) => {
              return (
                <CalculatorFieldset
                  bq={bq}
                  key={bq.id}
                  handleInputs={handleInputs}
                  handleUnits={handleUnits}
                  handleRemoveInput={handleRemoveInput}
                />
              );
            })}
          </div>
          {/* ---------------------- BOTTOM TOOLBAR ----------------------*/}
          <div className="calculator__toolbar">
            <div style={{ display: "flex" }}>
              <button
                className="rectangular-button small inverse-color"
                onClick={handleReceipt}
                type="button"
                data-tooltip="save to receipt"
              >
                <FaIcons.FaReceipt />
              </button>
              <button
                type="button"
                onClick={() => duplicateUnit(calc)}
                className="rectangular-button small inverse-color"
                data-tooltip="duplicate calc"
              >
                <FaIcons.FaClone />
              </button>
              <button
                className="rectangular-button inverse-color small"
                type="button"
                onClick={handleAddInput}
                data-tooltip="add field"
              >
                <FaIcons.FaPlus />
              </button>
            </div>

            <div
              className="calculator__decimal"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <span>no. decimals: {calc.noDecimals}</span>
              <div style={{ display: "flex" }}>
                <div data-tooltip="decrease decimals">
                  <button
                    className="calculator__decimal-button rectangular-button smaller inverse-color"
                    type="button"
                    onClick={(e) => handleDecimal(e, "decrease")}
                  >
                    <FaIcons.FaMinus />
                  </button>
                </div>
                <div data-tooltip="increase decimals">
                  <button
                    className="calculator__decimal-button  rectangular-button smaller inverse-color"
                    type="button"
                    onClick={(e) => handleDecimal(e, "increase")}
                  >
                    <FaIcons.FaPlus />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* <span className="calculator__updatedAt">
                updated at: {calc.updatedAt}
              </span> */}
        </form>
      </div>
    );
  } catch (error) {
    return (
      <div
        id={calc.id}
        className="calculator"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "30rem",
        }}
      >
        <h2 style={{ color: "var(--color-error)" }}>Calculator Corrupt</h2>
        <p style={{ textAlign: "center", maxWidth: "30rem" }}>
          Your can either delete the corrupted calculator, or export your data
          and send it to our superuser in the feedback form below
        </p>
        <button
          className="rectangular-button inverse-color"
          style={{ backgroundSize: "100rem" }}
          type="button"
          onClick={() => removeUnit(calc.id)}
        >
          Delete corrupted calculator
        </button>
      </div>
    );
  }
}
